import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import useGenerateGreetMutation from '../hooks/useGenerateGreetMutation';
import DSDialog from '../../../../design-system/DSDialog';
import DSDialogTitle from '../../../../design-system/DSDialogTitle';
import DSDialogContent from '../../../../design-system/DSDialogContent';
import DSSecondaryButton from '../../../../design-system/DSSecondaryButton';
import DSPrimaryButton from '../../../../design-system/DSPrimaryButton';
import DSTextField from '../../../../design-system/DSTextField';
import DSDialogActions from '../../../../design-system/DSDialogActions';
import { DSTypography } from '../../../../design-system/Typography';
import GreetingPreview from './GreetingPreview';
import ToneSelector from './ToneSelector';
import DSBox from '../../../../design-system/DSBox';
import DSButton from '../../../../design-system/DSButton';
import { GreetingTone } from '../../../../api/gift';
import { KLYDO_BLACK, KLYDO_WHITE } from '../../../../consts/colors';

type GenerateGreetModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (greet: string) => void;
};

const GenerateGreetDialog: React.FC<GenerateGreetModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [recipientName, setRecipientName] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [selectedTone, setSelectedTone] = useState<GreetingTone | undefined>(
    undefined,
  );
  const { palette } = useTheme();
  const [generatedGreet, setGeneratedGreet] = useState('');

  const {
    mutate: generateGreetMutation,
    data: greet,
    isPending,
    isError,
  } = useGenerateGreetMutation({
    onSuccess: (data) => setGeneratedGreet(data),
  });

  const clearState = () => {
    setGeneratedGreet('');
    setRecipientName('');
    setEventDescription('');
    setSelectedTone(undefined);
  };

  const handleSave = () => {
    onSave(generatedGreet);
    clearState();
  };

  const handleCancel = () => {
    clearState();
    onClose();
  };

  return (
    <DSDialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DSDialogTitle>Generate Greeting</DSDialogTitle>
      <DSDialogContent>
        <DSTextField
          fullWidth
          label="Recipient Name"
          margin="dense"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
        />
        <DSTextField
          fullWidth
          label="Event Description"
          margin="dense"
          value={eventDescription}
          onChange={(e) => setEventDescription(e.target.value)}
        />
        <ToneSelector value={selectedTone} onChange={setSelectedTone} />
        <DSBox sx={{ pt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <DSTypography>Greeting Preview:</DSTypography>
          <DSButton
            onClick={() =>
              generateGreetMutation({
                recipientName,
                eventDescription,
                tone: selectedTone,
              })
            }
            disabled={!recipientName || !eventDescription || isPending}
            sx={{
              background: KLYDO_WHITE,
              color: KLYDO_BLACK,
              borderRadius: 70,
              padding: '4px 12px',
              fontFamily: 'Gilroy-Bold',
              '&.Mui-disabled': {
                backgroundColor: palette.action.disabledBackground,
                color: palette.action.disabled,
              },
            }}
          >
            Generate
          </DSButton>
        </DSBox>
        <GreetingPreview
          greet={greet ?? null}
          isLoading={isPending}
          isError={isError}
        />
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={handleCancel}>Cancel</DSSecondaryButton>
        <DSPrimaryButton onClick={handleSave} disabled={!generatedGreet}>
          Save
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default GenerateGreetDialog;
